import { useState, useCallback } from 'react';
import { Save, User, Lock, Bell, Palette, Globe, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useApp, useAuth } from '../context/AuthContext';
import { authService, type AuthError } from '../services/authService';

// Password strength requirements
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REQUIREMENTS = {
  minLength: { label: 'At least 8 characters', test: (p: string) => p.length >= PASSWORD_MIN_LENGTH },
  hasUppercase: { label: 'At least one uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  hasLowercase: { label: 'At least one lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  hasNumber: { label: 'At least one number', test: (p: string) => /[0-9]/.test(p) },
  hasSpecial: { label: 'At least one special character (!@#$%^&*)', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
};

export default function Settings() {
  const { state, dispatch } = useApp();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form states
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || '',
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: false,
    reservationAlerts: true,
    reviewAlerts: true,
    systemAlerts: true,
  });

  const [appearance, setAppearance] = useState({
    theme: state.theme,
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
  });

  // Validate password against all requirements
  const validatePassword = useCallback((password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    for (const [, requirement] of Object.entries(PASSWORD_REQUIREMENTS)) {
      if (!requirement.test(password)) {
        errors.push(requirement.label);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }, []);

  // Check password strength for indicator
  const getPasswordStrength = useCallback((password: string): { strength: number; label: string; color: string } => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    for (const [, requirement] of Object.entries(PASSWORD_REQUIREMENTS)) {
      if (requirement.test(password)) strength++;
    }
    
    const total = Object.keys(PASSWORD_REQUIREMENTS).length;
    const percentage = (strength / total) * 100;
    
    if (percentage <= 20) return { strength: percentage, label: 'Very Weak', color: 'bg-red-500' };
    if (percentage <= 40) return { strength: percentage, label: 'Weak', color: 'bg-orange-500' };
    if (percentage <= 60) return { strength: percentage, label: 'Fair', color: 'bg-yellow-500' };
    if (percentage <= 80) return { strength: percentage, label: 'Strong', color: 'bg-blue-500' };
    return { strength: percentage, label: 'Very Strong', color: 'bg-green-500' };
  }, []);

  // Handle security/password change
  const handleSecuritySave = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      // Validation checks
      if (!security.currentPassword.trim()) {
        setMessage({ type: 'error', text: 'Current password is required.' });
        return;
      }

      if (!security.newPassword.trim()) {
        setMessage({ type: 'error', text: 'New password is required.' });
        return;
      }

      // Check if current and new password are the same
      if (security.currentPassword === security.newPassword) {
        setMessage({ type: 'error', text: 'New password must be different from current password.' });
        return;
      }

      // Validate password strength
      const validation = validatePassword(security.newPassword);
      if (!validation.isValid) {
        setMessage({ 
          type: 'error', 
          text: `Password requirements not met: ${validation.errors[0]}` 
        });
        return;
      }

      // Check if passwords match
      if (security.newPassword !== security.confirmPassword) {
        setMessage({ type: 'error', text: 'New password and confirmation do not match.' });
        return;
      }

      // Call the API
      await authService.changePassword(
        security.currentPassword,
        security.newPassword,
        user?.email
      );

      // Clear form on success
      setSecurity({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (error) {
      const authError = error as AuthError;
      let errorMessage = 'Failed to change password. Please try again.';
      
      if (authError.type === 'INVALID_CREDENTIALS') {
        errorMessage = 'Current password is incorrect.';
      } else if (authError.type === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (authError.message) {
        errorMessage = authError.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (section: string) => {
    if (section === 'security') {
      await handleSecuritySave();
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // Simulate API call for other sections
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (section === 'profile') {
        // Update user profile
      } else if (section === 'notifications') {
        // Update notification preferences
      } else if (section === 'appearance') {
        // Update appearance settings
        if (appearance.theme !== state.theme) {
          dispatch({ type: 'SET_THEME', payload: appearance.theme as 'light' | 'dark' });
        }
      }
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength indicator component
  const passwordStrength = getPasswordStrength(security.newPassword);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Appearance', icon: Palette },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-64">
          <nav className="space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {message && (
            <div className={`p-4 rounded-lg mb-6 ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>
                  Update your account profile information and email address.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profile.name}
                      onChange={(e) => setProfile({...profile, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({...profile, email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    value={profile.role}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-muted-foreground">
                    Contact an administrator to change your role
                  </p>
                </div>
                <div className="pt-4">
                  <Button onClick={() => handleSave('profile')} disabled={isLoading} variant="blueBg">
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Profile
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <CardTitle>Password & Security</CardTitle>
                <CardDescription>
                  Update your password and security settings. Use a strong, unique password.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Password */}
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={security.currentPassword}
                      onChange={(e) => setSecurity({...security, currentPassword: e.target.value})}
                      placeholder="Enter your current password"
                      className="pr-10"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      tabIndex={-1}
                      aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={security.newPassword}
                      onChange={(e) => setSecurity({...security, newPassword: e.target.value})}
                      placeholder="Enter your new password"
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      tabIndex={-1}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {security.newPassword && (
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Password Strength:</span>
                        <span className={`font-medium ${
                          passwordStrength.strength <= 40 ? 'text-red-600' :
                          passwordStrength.strength <= 60 ? 'text-yellow-600' :
                          passwordStrength.strength <= 80 ? 'text-blue-600' : 'text-green-600'
                        }`}>
                          {passwordStrength.label}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: `${passwordStrength.strength}%` }}
                        />
                      </div>

                      {/* Password Requirements Checklist */}
                      <div className="mt-3 space-y-1">
                        {Object.entries(PASSWORD_REQUIREMENTS).map(([key, requirement]) => {
                          const isMet = requirement.test(security.newPassword);
                          return (
                            <div key={key} className="flex items-center gap-2 text-xs">
                              {isMet ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <AlertCircle className="h-3.5 w-3.5 text-gray-400" />
                              )}
                              <span className={isMet ? 'text-green-700' : 'text-gray-500'}>
                                {requirement.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={security.confirmPassword}
                      onChange={(e) => setSecurity({...security, confirmPassword: e.target.value})}
                      placeholder="Confirm your new password"
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Password Match Indicator */}
                  {security.confirmPassword && security.newPassword && (
                    <div className="flex items-center gap-2 text-xs mt-1">
                      {security.newPassword === security.confirmPassword ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-green-700">Passwords match</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                          <span className="text-red-700">Passwords do not match</span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => handleSave('security')} 
                    disabled={isLoading || !security.currentPassword || !security.newPassword || !security.confirmPassword}
                    className="w-full sm:w-auto"
                    variant="blueBg"
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    ) : (
                      <Lock className="h-4 w-4 mr-2" />
                    )}
                    Update Password
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>
                  Choose what notifications you want to receive.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(notifications).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Receive notifications for {key.toLowerCase()}
                      </p>
                    </div>
                    <Button
                      variant={value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNotifications({
                        ...notifications,
                        [key]: !value
                      })}
                    >
                      {value ? 'On' : 'Off'}
                    </Button>
                  </div>
                ))}
                <div className="pt-4">
                  <Button onClick={() => handleSave('notifications')} disabled={isLoading}>
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <Card>
              <CardHeader>
                <CardTitle>Appearance & Localization</CardTitle>
                <CardDescription>
                  Customize the look and feel of your dashboard.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="theme">Theme</Label>
                    <div className="flex space-x-2">
                      <Button
                        variant={appearance.theme === 'light' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAppearance({...appearance, theme: 'light'})}
                      >
                        Light
                      </Button>
                      <Button
                        variant={appearance.theme === 'dark' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAppearance({...appearance, theme: 'dark'})}
                      >
                        Dark
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language">Language</Label>
                    <Button variant="outline" className="w-full justify-start">
                      <Globe className="h-4 w-4 mr-2" />
                      English (US)
                    </Button>
                  </div>
                </div>
                <div className="pt-4">
                  <Button onClick={() => handleSave('appearance')} disabled={isLoading} variant="blueBg">
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Appearance
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
